import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ErrorBoundary from '../components/ui/ErrorBoundary';
import FieldError from '../components/ui/FieldError';
import { INDIA_STATES } from '../constants/indiaStates';

import useApi from '../hooks/useApi';

const ReviewSubmit = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isValidatingData, setIsValidatingData] = useState(true);

  const navigate = useNavigate();
  const { user, updateUser, setConfirmationNumberValue } = useAuth();
  const api = useApi();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if all required form data exists
  useEffect(() => {
    if (!user) return;

    // Check if business type is missing
    if (!user.businessType) {
      navigate('/business-type', { replace: true });
      return;
    }

    // Check if representative details are missing
    if (!user.representativeDetails || !user.representativeDetails.firstName || !user.representativeDetails.lastName) {
      navigate('/representative-details', { replace: true });
      return;
    }

    // Check if shop details are missing
    if (!user.shopDetails || !user.shopDetails.shopName || !user.shopDetails.shopDescription) {
      navigate('/shop-details', { replace: true });
      return;
    }

    // All data is valid, allow rendering
    setIsValidatingData(false);
  }, [user, navigate]);

  const handleEditSection = (section) => {
    switch (section) {
      case 'business':
        navigate('/business-type');
        break;
      case 'representative':
        navigate('/representative-details');
        break;
      case 'shop':
        navigate('/shop-details');
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    // Validate that all required data is present
    if (!user.businessType) {
      setError('Business type is required. Please go back and complete the business type selection.');
      return;
    }
    if (!user.representativeDetails) {
      setError('Representative details are required. Please go back and complete the representative details.');
      return;
    }
    if (!user.shopDetails) {
      setError('Shop details are required. Please go back and complete the shop details.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Build payload expected by backend
      const payload = {
        business_type: user.businessType,
        business_title: user.businessTitle,
        ...user.representativeDetails && {
          rep_first_name: user.representativeDetails.firstName,
          rep_last_name: user.representativeDetails.lastName,
          rep_email: user.representativeDetails.email,
          rep_phone: user.representativeDetails.phone,
          rep_addr_line1: user.representativeDetails.addressLine1,
          rep_addr_line2: user.representativeDetails.addressLine2,
          rep_city: user.representativeDetails.city,
          rep_state: user.representativeDetails.state,
          rep_zip_code: user.representativeDetails.zipCode,
          rep_country: user.representativeDetails.country,
        },
        ...user.shopDetails && {
          shop_name: user.shopDetails.shopName,
          shop_description: user.shopDetails.shopDescription,
          shop_type: user.shopDetails.shopType,
          establishment_year: user.shopDetails.establishmentYear,
          website: user.shopDetails.website,
          biz_addr_line1: user.shopDetails.addressLine1,
          biz_addr_line2: user.shopDetails.addressLine2,
          biz_city: user.shopDetails.city,
          biz_state: user.shopDetails.state,
          biz_zip_code: user.shopDetails.zipCode,
          biz_country: user.shopDetails.country,
        }
      };

      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      // POST to backend
      const response = await api.post(`/vendor/register`, payload);

      const applicationData = { ...payload, submittedAt: new Date().toISOString(), status: 'pending_review' };

      // Store confirmation number if available in response
      if (response?.confirmation_number) {
        setConfirmationNumberValue(response.confirmation_number);
      }

      // Update user status
      updateUser({
        applicationSubmitted: true,
        applicationData: applicationData
      });

      setIsSubmitted(true);
      setSuccess('Your application has been submitted successfully!');

      // Redirect to application submitted page after success
      setTimeout(() => {
        navigate('/application-submitted');
      }, 3000);

    } catch (err) {
      setError('Failed to submit application. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatBusinessType = (type) => {
    const types = {
      'individual': 'Individual',
      'sole-proprietorship': 'Sole Proprietorship',
      'corporation': 'Corporation',
      'partnership': 'Partnership'
    };
    return types[type] || type;
  };

  const getStateName = (stateCode) => {
    const state = INDIA_STATES.find(s => s.value === stateCode);
    return state ? state.label : stateCode;
  };


  // Show loading while validating data
  if (isValidatingData) {
    return (
      <div className="form-container">
        <div className="form-content">
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
              Validating your application data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="form-container">
        <div className="form-content">
          <div className="success-message">
            <h2>Application Submitted!</h2>
            <p>{success}</p>
            <p>You will be redirected shortly...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="form-container">
        <div className="form-content">
          {/* Progress Indicator */}
          <div className="progress-indicator">
            <div className="progress-step completed">
              <div className="step-number">1</div>
              <div className="step-label">Business Type</div>
            </div>
            <div className="progress-line completed"></div>
            <div className="progress-step completed">
              <div className="step-number">2</div>
              <div className="step-label">Representative</div>
            </div>
            <div className="progress-line completed"></div>
            <div className="progress-step completed">
              <div className="step-number">3</div>
              <div className="step-label">Shop Details</div>
            </div>
            <div className="progress-line completed"></div>
            <div className="progress-step active">
              <div className="step-number">4</div>
              <div className="step-label">Review</div>
            </div>
          </div>

          <div className="form-header">
            <h1 className="form-title">Review & Submit</h1>
            <p className="form-subtitle">
              Please review all your information before submitting your application
            </p>
          </div>

          <div className="review-content">
            {/* Business Type Section */}
            <div className="review-section">
              <div className="section-header">
                <h3>Business Type</h3>
                <button
                  onClick={() => handleEditSection('business')}
                  className="edit-btn"
                >
                  Edit
                </button>
              </div>
              <div className="review-item">
                <span className="label">Type:</span>
                <span className="value">{user.businessType ? formatBusinessType(user.businessType) : 'No data available'}</span>
              </div>
            </div>

            {/* Representative Details Section */}
            {user.representativeDetails && (
              <div className="review-section">
                <div className="section-header">
                  <h3>Primary Representative Details</h3>
                  <button
                    onClick={() => handleEditSection('representative')}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                </div>
                <div className="review-grid">
                  <div className="review-item">
                    <span className="label">Name:</span>
                    <span className="value">
                      {user.representativeDetails.firstName} {user.representativeDetails.lastName}
                    </span>
                  </div>
                  <div className="review-item">
                    <span className="label">Email:</span>
                    <span className="value">{user.representativeDetails.email}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">Phone:</span>
                    <span className="value">{user.representativeDetails.phone}</span>
                  </div>
                  <div className="review-item full-width">
                    <span className="label">Street Address:</span>
                    <span className="value">{user.representativeDetails.addressLine1}</span>
                  </div>
                  {user.representativeDetails.addressLine2 && (
                    <div className="review-item full-width">
                      <span className="label">Apartment/Suite:</span>
                      <span className="value">{user.representativeDetails.addressLine2}</span>
                    </div>
                  )}
                  <div className="review-item">
                    <span className="label">City:</span>
                    <span className="value">{user.representativeDetails.city}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">State:</span>
                    <span className="value">{getStateName(user.representativeDetails.state)}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">PIN Code:</span>
                    <span className="value">{user.representativeDetails.zipCode}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">Country:</span>
                    <span className="value">{user.representativeDetails.country}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Shop Details Section */}
            {user.shopDetails && (
              <div className="review-section">
                <div className="section-header">
                  <h3>Shop Details</h3>
                  <button
                    onClick={() => handleEditSection('shop')}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                </div>
                <div className="review-grid">
                  <div className="review-item full-width">
                    <span className="label">Shop Name:</span>
                    <span className="value">{user.shopDetails.shopName}</span>
                  </div>
                  <div className="review-item full-width">
                    <span className="label">Description:</span>
                    <span className="value">{user.shopDetails.shopDescription}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">Category:</span>
                    <span className="value">{user.shopDetails.shopType}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">Established:</span>
                    <span className="value">{user.shopDetails.establishmentYear}</span>
                  </div>
                  {user.shopDetails.website && (
                    <div className="review-item">
                      <span className="label">Website:</span>
                      <span className="value">{user.shopDetails.website}</span>
                    </div>
                  )}
                  {user.shopDetails.socialMedia?.facebook && (
                    <div className="review-item">
                      <span className="label">Facebook:</span>
                      <span className="value">{user.shopDetails.socialMedia.facebook}</span>
                    </div>
                  )}
                  {user.shopDetails.socialMedia?.instagram && (
                    <div className="review-item">
                      <span className="label">Instagram:</span>
                      <span className="value">{user.shopDetails.socialMedia.instagram}</span>
                    </div>
                  )}
                  {user.shopDetails.socialMedia?.twitter && (
                    <div className="review-item">
                      <span className="label">Twitter:</span>
                      <span className="value">{user.shopDetails.socialMedia.twitter}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Business Address Section */}
            {user.shopDetails && (
              <div className="review-section">
                <div className="section-header">
                  <h3>Business Address</h3>
                  <button
                    onClick={() => handleEditSection('shop')}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                </div>
                <div className="review-grid">
                  <div className="review-item full-width">
                    <span className="label">Street Address:</span>
                    <span className="value">{user.shopDetails.addressLine1}</span>
                  </div>
                  {user.shopDetails.addressLine2 && (
                    <div className="review-item full-width">
                      <span className="label">Apartment/Suite:</span>
                      <span className="value">{user.shopDetails.addressLine2}</span>
                    </div>
                  )}
                  <div className="review-item">
                    <span className="label">City:</span>
                    <span className="value">{user.shopDetails.city}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">State:</span>
                    <span className="value">{getStateName(user.shopDetails.state)}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">PIN Code:</span>
                    <span className="value">{user.shopDetails.zipCode}</span>
                  </div>
                  <div className="review-item">
                    <span className="label">Country:</span>
                    <span className="value">{user.shopDetails.country}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="review-footer">
            <div className="terms-section">
              <p className="terms-text">
                By continuing you are agreeing to{' '}
                <a href="/terms-and-conditions" className="terms-link" target="_blank" rel="noopener noreferrer">Terms and conditions</a> and{' '}
                <a href="/privacy-policy" className="terms-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </p>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/shop-details')}
                className="btn btn-outline back-btn"
                disabled={isLoading}
              >
                ← Back
              </button>

              <FieldError error={error} />


              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className={`btn btn-primary submit-btn ${isLoading ? 'loading' : ''}`}
              >
                {isLoading ? (
                  <>
                    <span className="button-spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ReviewSubmit;
